import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Copy, Download, KeyRound, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatDate = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  return isNaN(d) ? iso : d.toLocaleString("ro-RO");
};

export const PdfPasswords = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_URL}/api/form-submissions-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      toast.error("Nu am putut încărca parolele PDF");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const copy = (value) => {
    navigator.clipboard.writeText(value);
    toast.success("Parolă copiată");
  };

  const download = async (item) => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_URL}/api/form-submissions/${item.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Cerere_${(item.client_name || "client").replace(/\s+/g, "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("PDF-ul nu mai este disponibil pe server");
    }
  };

  return (
    <Card className="mb-6" data-testid="pdf-passwords-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5" /> Parole PDF
            </CardTitle>
            <CardDescription>
              PDF-urile trimise pe email sunt criptate. Parola nu se trimite pe email — o găsiți aici.
            </CardDescription>
          </div>
          <Button variant="outline" onClick={load} data-testid="pdf-passwords-refresh">
            <RefreshCw className="h-4 w-4 mr-2" /> Reîmprospătează
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-gray-500">Se încarcă...</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-gray-500" data-testid="pdf-passwords-empty">
            Nu există cereri trimise încă.
          </p>
        ) : (
          <div className="space-y-3" data-testid="pdf-passwords-list">
            {items.map((item) => (
              <div key={item.id} className="flex flex-wrap items-center justify-between gap-3 p-3 border rounded-lg">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium truncate">{item.client_name || "Client"}</span>
                    {item.email_status === "failed" ? (
                      <Badge variant="destructive">Email eșuat</Badge>
                    ) : item.email_status === "sent" ? (
                      <Badge className="bg-green-100 text-green-800">Trimis</Badge>
                    ) : (
                      <Badge variant="secondary">În curs</Badge>
                    )}
                  </div>
                  <div className="text-xs text-gray-500">{formatDate(item.created_at)}</div>
                </div>
                <div className="flex items-center gap-2">
                  {item.pdf_password ? (
                    <>
                      <code className="px-2 py-1 bg-gray-100 rounded font-mono text-sm" data-testid={`pdf-password-${item.id}`}>
                        {item.pdf_password}
                      </code>
                      <Button variant="outline" size="sm" onClick={() => copy(item.pdf_password)}
                              data-testid={`pdf-password-copy-${item.id}`}>
                        <Copy className="h-4 w-4" />
                      </Button>
                    </>
                  ) : (
                    <span className="text-xs text-gray-500">
                      {item.pdf_password_source === "broker" ? "Parola dvs. din Setări" : "—"}
                    </span>
                  )}
                  {item.email_status === "failed" && (
                    <Button variant="outline" size="sm" onClick={() => download(item)}
                            data-testid={`pdf-download-${item.id}`}>
                      <Download className="h-4 w-4 mr-1" /> PDF
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PdfPasswords;
